import { Button } from '@brightdrop/bd-ui';
import {
  Avatar,
  Badge,
  Box,
  Collapse,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  type AlertRecord,
  useGetAlertsByGroupIdQuery,
} from '~/api/generated-api';
import ChevronDownIcon from '~/assets/icons/new/chevron-down.svg?react';
import Spinner from '~/common/components/Spinner/Spinner';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import { AlertCategory } from '~/common/models/alert.model';
import { getNotificationIcon } from '~/routes/settings/settings.utils';
import { selectActingProfileOrganization } from '~/store/profile/profileSlice.selectors';
import { mapAlertEventNameWithCount } from '~/utils/alerts/alert.mappers';
import { useAlertsPagination } from '../../../hooks/useAlertsPagination';
import { useSettingsNotificationOption } from '../../../utils/notification.utils';
import NotificationActionsMenu from '../../Buttons/NotificationActionsMenu';
import NotificationDateTime from '../../NotificationDateTime';
import NotificationList from '../../NotificationList/NotificationList';
import { useStyles } from '../NotificationCard.styles';

type GroupNotificationCardProps = {
  item: AlertRecord;
  context?: 'popover' | 'page';
  onClose?: () => void;
};

function GroupNotificationCard({
  item,
  context = 'page',
  onClose,
}: GroupNotificationCardProps) {
  // Subscribe to i18n so imperative `translateText(...)` updates after locale changes.
  useTranslation(['settings', 'common']);
  const { classes } = useStyles();
  const theme = useTheme();

  const orgId = useSelector(selectActingProfileOrganization)?.id;
  const [groupExpanded, setGroupExpanded] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [localAlertCount, setLocalAlertCount] = useState<number>(
    context === 'popover' ? item.feedActiveCount : item.alertCount
  );

  const groupLabel = `${localAlertCount} ${
    item.groupName
      ? item.groupName
      : item.lastGroupEvent?.event?.category === AlertCategory.DIAGNOSTIC
        ? translateText('common:diagnostics')
        : item.lastGroupEvent?.event?.event
          ? mapAlertEventNameWithCount(
              item.lastGroupEvent.event.event,
              localAlertCount
            )
          : DEFAULT_EMPTY_VALUE
  }`;

  const [token, setToken] = useState<string | undefined>(undefined);
  const { data, isLoading, isFetching } = useGetAlertsByGroupIdQuery(
    {
      groupId: item.id,
      ...(context === 'popover' && { dismissed: false }),
      size: 20,
      'cs-organization': orgId ?? '',
      'cs-continuation-token': token,
    },
    {
      skip: !groupExpanded,
      refetchOnMountOrArgChange: true,
    }
  );

  const { alerts, hasMoreAlerts, handleRemoveNotification } =
    useAlertsPagination({ data });

  const handleDismissFromGroup = (alertId: string) => {
    setIsDismissing(true);
    // Remove the dismissed notification from cached data
    handleRemoveNotification(alertId);
    // Update local alert count when a notification is dismissed from within the group
    setLocalAlertCount((prevCount) => {
      const newCount = Math.max(0, prevCount - 1);
      if (newCount === 0) {
        setGroupExpanded(false);
      }
      return newCount;
    });
    setTimeout(() => setIsDismissing(false), 500);
  };

  const handleShowMoreClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setGroupExpanded(true);
  };

  const handleLoadMoreClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!hasMoreAlerts || isLoading || !data?.result?.continuation_token) {
      return;
    }

    setToken(data?.result?.continuation_token);
  };

  const settingsOption = useSettingsNotificationOption(item);

  useEffect(() => {
    setLocalAlertCount(
      context === 'popover' ? item.feedActiveCount : item.alertCount
    );
  }, [item.alertCount, item.feedActiveCount, context]);

  return (
    <Box mb={groupExpanded ? '16px' : '0px'}>
      {/* Collapsed needs to be outside of checks in order for collapse animation to work */}
      <Collapse in={groupExpanded} timeout={0}>
        {/* Show group notifications when group card isxpanded */}
        {groupExpanded && (
          <>
            <NotificationList
              title={groupLabel}
              items={alerts}
              type="group"
              context={context}
              showLessCallback={() => {
                setGroupExpanded(false);
              }}
              onDismissNotification={handleDismissFromGroup}
              onClose={onClose}
            />
            {hasMoreAlerts && !isDismissing && (
              <Box className={classes.loadMoreContainer}>
                <span className={classes.loadMoreButtonWrapper}>
                  <Button
                    variant="secondary"
                    onClick={handleLoadMoreClick}
                    isLoading={isLoading || isFetching}
                  >
                    <TranslatedLabel
                      translationKey="common:loadMore"
                      typographyVariant="label_medium"
                    />
                  </Button>
                </span>
              </Box>
            )}
            <Spinner show={isLoading || isFetching || isDismissing} />
          </>
        )}
      </Collapse>

      {/* Show group card when group notifications are collapsed */}
      {!groupExpanded && localAlertCount > 0 && (
        <ListItem
          className={
            classes[
              'rt--notifications--cmp--list--list-item--notification-item--group-item'
            ]
          }
          sx={{ width: context === 'popover' ? '468px' : '100%' }}
          divider={true}
          secondaryAction={
            context === 'page' && (
              <NotificationActionsMenu options={[settingsOption]} />
            )
          }
          data-testid={`group-notification-item--${item.id}`}
        >
          <ListItemAvatar>
            <Avatar
              className={`${classes.icon} ${item.read ? '' : classes.unRead}`}
            >
              <img
                src={getNotificationIcon(
                  item?.lastGroupEvent?.event?.event,
                  item?.lastGroupEvent?.event?.category
                )}
                alt={
                  item?.lastGroupEvent?.event?.event ||
                  translateText('settings:notificationList.genericNotification')
                }
                width="24"
                height="24"
              />
            </Avatar>
            {!item.read && context === 'page' && (
              <Badge
                data-testid="unread-notification-icon"
                aria-label={translateText(
                  'settings:notificationList.unreadNotification'
                )}
                overlap="circular"
                badgeContent=" "
                variant="dot"
                className={classes.badge}
              />
            )}
          </ListItemAvatar>
          <ListItemText
            data-testid={`group-notification-item--${item.id}-label`}
            primary={
              <Typography variant="body_regular">{groupLabel}</Typography>
            }
            secondary={
              <>
                <Typography
                  variant="label_regular"
                  color={theme.new.color.textSecondary}
                >
                  <NotificationDateTime value={item.lastEventDateTime} />
                </Typography>
                <span className={classes.showMoreButtonWrapper}>
                  <Button
                    variant="button-link"
                    endIcon={<ChevronDownIcon />}
                    iconColorRule="stroke"
                    onClick={handleShowMoreClick}
                  >
                    <TranslatedLabel
                      translationKey="common:showMore"
                      typographyVariant="label_medium"
                    />
                  </Button>
                </span>
              </>
            }
          />
        </ListItem>
      )}
    </Box>
  );
}

export default GroupNotificationCard;
